import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { DATE_TIME_FORMAT_TYPES } from "../../configs/constants/DateTimeFormatTypes";
import { JOB_STATUS } from "../../configs/constants/JobStatus";
import Job from "../../models/Job";
import User from "../../models/User";
import { denialReasonLabel } from "../../utils/job.utils";
import Tag, { TagProps } from "./Tag/Tag";

const JobStatusTag: React.FC<{ job?: Job; size?: TagProps["size"] }> = ({ job, size }) => {
	const { t } = useTranslation();

	if (!job) return null;

	return (
		<>
			{job.status === JOB_STATUS.BOOKED && <Tag color="green" size={size} text={t("BOOKED")} />}
			{job.status === JOB_STATUS.BOOKED_WITH_UNMANNED_SHIFTS && (
				<Tag color="orange" size={size} text={t("job.jobStatus.bookedWithUnmannedShifts")} />
			)}
			{job.status === JOB_STATUS.DECLINED && (
				<Tag
					size={size}
					color="danger"
					text={t("job.jobStatus.declined")}
					textOnHover={declinedHoverText(job, t)}
				/>
			)}
			{job.status === JOB_STATUS.CANCELLED && (
				<Tag
					size={size}
					color="danger"
					text={t("job.jobStatus.cancelled")}
					textOnHover={t("job.jobStatus.cancelledBy", {
						name: fullNameOrId(job.metadata.cancelledBy),
						date: dayjs(job.metadata.cancelled).format(DATE_TIME_FORMAT_TYPES.DATE_TIME),
					})}
				/>
			)}
			{job.status === JOB_STATUS.DELETED && (
				<Tag color="danger" size={size} text={t("job.jobStatus.deleted")} />
			)}
		</>
	);
};

function declinedHoverText(job: Job, t: ReturnType<typeof useTranslation>["t"]) {
	const declinedBy = t("job.jobStatus.declinedBy", {
		name: fullNameOrId(job.metadata.declinedBy),
		date: dayjs(job.metadata.declined).format(DATE_TIME_FORMAT_TYPES.DATE_TIME),
	});

	const reasonLabel = denialReasonLabel(job.metadata.denialReason);

	if (!reasonLabel) return declinedBy;

	return `${declinedBy}\n${t("job.jobStatus.declinedReason", { reason: reasonLabel })}`;
}

function fullNameOrId(user?: Partial<User>) {
	if (!user) {
		return "";
	} else if (user.firstName && user.lastName) {
		const isCustomer = user.roles?.includes("CUSTOMER_USER");
		return user.firstName + " " + user.lastName + (isCustomer ? " (kund)" : "");
	}
	return user.id;
}

export default JobStatusTag;
